import type {
  Milestone, PlanSummary, PortfolioResponse, Project, ProjectHealth } from '@/types/domain';

export function assertPortfolioResponse(value: unknown): asserts value is PortfolioResponse {
  if (!value || typeof value !== 'object') throw new Error('项目组合响应不是对象');
  const payload = value as Partial<PortfolioResponse>;
  for (const key of ['projects', 'summaries', 'milestones', 'health'] as const) {
    if (!Array.isArray(payload[key])) throw new Error(`项目组合响应缺少数组字段 ${key}`);
  }
  if (!payload.metrics || typeof payload.metrics !== 'object') throw new Error('项目组合响应缺少 metrics');
  if (!payload.asOf) throw new Error('项目组合响应缺少 asOf');
}

export function summaryFor(payload: PortfolioResponse, projectId: Project['id']): PlanSummary | undefined {
  return payload.summaries.find((item) => String(item.projectId) === String(projectId));
}

/**
 * 项目灯的中文（0911 起叫红黄绿，不叫异常 / 关注 / 正常）。
 * 全站只在这一处翻译；键值仍是 risk / warning / normal，CSS 和筛选都不用动。
 */
export function healthLabel(status: string | null | undefined): string {
  if (status === 'risk') return '红灯';
  if (status === 'warning') return '黄灯';
  if (status === 'normal') return '绿灯';
  return '未评估';
}

/** 项目灯原因代号 → 中文（后端 project-rag.ts 的 reasons） */
export const HEALTH_REASON_LABELS: Readonly<Record<string, string>> = {
  issue_at_top: '有问题升到管理层',
  issue_at_functional: '有问题升到部门负责人',
  customer_escalation: '客户升级投诉',
  customer_complaint: '客户口头 / 书面投诉',
  missing_plan: '没有项目计划',
  milestone_overdue_no_plan: '里程碑逾期、没有挽回计划',
  milestone_overdue_with_plan: '里程碑逾期、已登记挽回计划',
  declared_risk: '项目经理自评红灯',
  declared_warning: '项目经理自评黄灯',
  declaration_below_facts: '自评比事实轻，按事实定色',
};

/**
 * 数据卫生代号 → 中文（后端 portfolio-health.service 的 dataIssues）。
 * 不定色，只提示；健康度明细抽屉（ProjectHealthDetails）里这三条另有完整说明。
 */
export const HEALTH_DATA_ISSUE_LABELS: Readonly<Record<string, string>> = {
  missing_project_type: '没填项目类型',
  no_key_deliverables: '计划里没有关键交付物',
  duplicate_project_no: '项目编号重复',
  departed_owner: '有离职人员名下的活没接手', // 1006
};

/** 灯的原因、数据问题都从这里翻；没配中文的代号原样露出，别吞掉 */
export function healthReasonText(code: string): string {
  return HEALTH_REASON_LABELS[code] ?? HEALTH_DATA_ISSUE_LABELS[code] ?? code;
}

export function healthFor(payload: PortfolioResponse, projectId: Project['id']): ProjectHealth | undefined {
  return payload.health.find((item) => String(item.projectId) === String(projectId));
}

/**
 * 项目进度。
 *
 * 原来按「状态=完成的计划节点 ÷ 已排期节点」算，但迁移过来的数据里 96% 的任务状态是空的，
 * 于是 X04-C 这种已经接近 SOP 的项目在首页显示 2%——那不是项目实况，是数据假象，
 * 管理层看到会直接判定系统的数不准。
 *
 * 改用「已达成里程碑 ÷ 里程碑总数」：里程碑的实际日期是真实维护的（X04-C 13 个里有 6 个有实际日期）。
 * 没有里程碑数据时回退到节点口径；两者都不可用时返回 null，由界面显示「数据不可用」而不是 0%。
 */
export function completionPercent(
  summary: PlanSummary | undefined,
  milestones?: Milestone[],
): number | null {
  if (milestones && milestones.length) {
    const reached = milestones.filter((item) => Boolean(item.actualDate)).length;
    return Math.max(0, Math.min(100, Math.round((reached / milestones.length) * 100)));
  }
  if (!summary || !Number.isFinite(summary.plannedNodes) || !Number.isFinite(summary.completedNodes)) return null;
  if (summary.plannedNodes <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((summary.completedNodes / summary.plannedNodes) * 100)));
}

/** 某个项目的里程碑 */
export function milestonesFor(payload: PortfolioResponse, projectId: Project['id']): Milestone[] {
  return payload.milestones.filter((item) => String(item.projectId) === String(projectId));
}

/**
 * 逾期里程碑：有计划日期、没有实际日期、计划日期早于口径日。
 *
 * 和后端 portfolio-health.service 用同一条规则。指挥台上那个「逾期里程碑 28」
 * 是后端算的，点进来筛出的项目必须按同一把尺子，否则数字对不上就没人信这个入口了。
 */
export function overdueMilestoneCount(
  payload: PortfolioResponse,
  projectId: Project['id'],
  asOf: string,
): number {
  return milestonesFor(payload, projectId).filter((milestone) =>
    milestone.planDate !== null && milestone.actualDate === null && milestone.planDate < asOf).length;
}

export function duplicateProjectNumbers(projects: Project[]): Set<string> {
  const counts = new Map<string, number>();
  for (const project of projects) counts.set(project.projectNo, (counts.get(project.projectNo) || 0) + 1);
  return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([projectNo]) => projectNo));
}

export function isArchivedProject(project: Project): boolean {
  const status = String(project.status ?? '').trim().toLowerCase();
  return Boolean(
    project.archived
    || project.deleted
    || ['archived', 'deleted', 'closed', '已归档', '已删除'].includes(status),
  );
}

/*
  项目处在哪个阶段：**已启动**、还是**草稿/报价还没正式开工**。

  0916 Tony：「项目组合里面的项目能否直观显示哪些是正常开发中的项目,
  哪些是草稿或者还未正式开始的项目」。

  这条判据后端早就有一份（`project-liveness.ts` 的 `NOT_STARTED_STATUSES`），
  0915 大屏/指挥台/总监看板三块看板对不上就是因为草稿项目混进了「项目总数」。
  前端原来在 PortfolioView 里又抄了一遍，两处迟早分叉——收到这里，
  指挥台和组合页都用它（[[one-judgement-per-concept]]）。

  归档/删除是第三种，不在这两档里：它们走 `isArchivedProject`。
*/
const NOT_STARTED_STATUSES: readonly string[] = ['draft', 'quotation', '草稿', '报价'];

/**
 * 已删除和已归档分开（0916 Tony:「项目状态标识不清楚」）。
 *
 * 项目组合页把两者一起过滤掉，分不分无所谓；但模块中心那张表**两种都在列**——
 * 线上 20 个项目里 11 个 deleted、1 个 archived，混在一起看不出哪个是真删了的。
 */
export type ProjectStage = 'started' | 'not-started' | 'archived' | 'deleted';

const DELETED_STATUSES: readonly string[] = ['deleted', '已删除'];

export function projectStage(project: { status?: string | null; archived?: boolean; deleted?: boolean }): ProjectStage {
  const status = String(project.status ?? '').trim();
  if (project.deleted === true || DELETED_STATUSES.includes(status)) return 'deleted';
  if (isArchivedProject(project as Project)) return 'archived';
  return NOT_STARTED_STATUSES.includes(status) ? 'not-started' : 'started';
}

/** 结束态：已删除 / 已归档。列表里要沉底、要压暗，别冒充在跑的项目 */
export function isEndedProject(project: { status?: string | null; archived?: boolean; deleted?: boolean }): boolean {
  const stage = projectStage(project);
  return stage === 'archived' || stage === 'deleted';
}

export function isStartedProject(project: { status?: string | null }): boolean {
  return !NOT_STARTED_STATUSES.includes(String(project.status ?? '').trim());
}

/** 卡片上那个小标签。开发中的也标出来——只标异常的话，人会以为没标的是"还没读到" */
export function projectStageLabel(project: { status?: string | null; archived?: boolean; deleted?: boolean }): string {
  const stage = projectStage(project);
  if (stage === 'deleted') return '已删除';
  if (stage === 'archived') return '已归档';
  if (stage === 'started') return '开发中';
  const status = String(project.status ?? '').trim();
  if (status === 'quotation' || status === '报价') return '报价中';
  return '草稿·未启动';
}
